import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardMedia,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Link,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExploreIcon from '@mui/icons-material/Explore';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import TagIcon from '@mui/icons-material/Tag';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LinkIcon from '@mui/icons-material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from '../AuthContext';
import api from '../api';
import Navigation from '../components/Navigation';
import VoiceRecorder from '../components/VoiceRecorder';
import LocationPicker from '../components/LocationPicker';
import GoodVibesDigest from '../components/GoodVibesDigest';
import { useToast } from '../hooks/useToast';

const Profile: React.FC = () => {
  const { user, logout, fetchProfile } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [uploading, setUploading] = useState<'photo' | 'voice' | null>(null);
  const [error, setError] = useState('');

  const { data: statusData } = useQuery({
    queryKey: ['conversations', 'status'],
    queryFn: () => api.get('/api/conversations/status').then(r => r.data),
  });
  const pendingFeedbackMatch = statusData?.has_pending_feedback && statusData?.pending_feedback?.length > 0
    ? statusData.pending_feedback[0].match_id
    : null;

  const { data: digestsData, refetch: refetchDigests } = useQuery({
    queryKey: ['compliments', 'digests'],
    queryFn: () => api.get('/api/compliments/digests').then(r => r.data),
  });

  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [removingTag, setRemovingTag] = useState<string | null>(null);
  const [savingLocation, setSavingLocation] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [savingPhone, setSavingPhone] = useState(false);

  const isProfileComplete = !!(user?.photo_url && user?.voice_recording_url && user?.location);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setUploading('photo');

    try {
      const formData = new FormData();
      formData.append('photo', file);

      await api.post('/api/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showToast('Photo uploaded successfully!');
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(null);
    }
  };

  const handleVoiceUpload = async (audioBlob: Blob) => {
    setError('');
    setUploading('voice');

    try {
      const formData = new FormData();
      const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
      formData.append('voice', audioFile);

      await api.post('/api/profile/voice', formData);

      showToast('Voice recording uploaded successfully!');
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload voice recording');
      throw err;
    } finally {
      setUploading(null);
    }
  };

  const handleAddTag = async (tagValue?: string) => {
    const tag = (tagValue ?? newTag).trim();
    if (!tag || addingTag) return;

    setError('');
    setAddingTag(true);
    setNewTag('');

    try {
      await api.post('/api/profile/tags', { tag });
      showToast('Tag added!');
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    setError('');
    setRemovingTag(tag);

    try {
      await api.delete(`/api/profile/tags/${encodeURIComponent(tag)}`);
      showToast('Tag removed successfully!');
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove tag');
    } finally {
      setRemovingTag(null);
    }
  };

  const handleLocationSave = async (lat: number, lng: number, radius: number) => {
    setError('');
    setSavingLocation(true);

    try {
      await api.post('/api/profile/location', { lat, lng, radius });
      showToast('Location saved successfully!');
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleCopyUrl = async () => {
    if (user?.url_id) {
      await navigator.clipboard.writeText(`${window.location.origin}/u/${user.url_id}`);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  };

  return (
    <>
      <Navigation />
      <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* Header row: title + completeness + private URL copy */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="caption" sx={{ color: "primary.main" }}>
            My Profile
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            {/* Completeness indicators */}
            {isProfileComplete ? (
              <Tooltip title="Profile complete" arrow><CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} /></Tooltip>
            ) : (
              <>
                <Tooltip title={user?.photo_url ? "Photo ✓" : "Photo missing"} arrow><PhotoCameraIcon sx={{ fontSize: 14, color: user?.photo_url ? "success.main" : "divider" }} /></Tooltip>
                <Tooltip title={user?.voice_recording_url ? "Voice ✓" : "Voice missing"} arrow><GraphicEqIcon sx={{ fontSize: 14, color: user?.voice_recording_url ? "success.main" : "divider" }} /></Tooltip>
                <Tooltip title={user?.tags?.length ? "Words ✓" : "Words missing"} arrow><TagIcon sx={{ fontSize: 14, color: user?.tags?.length ? "success.main" : "divider" }} /></Tooltip>
                <Tooltip title={user?.location ? "Location ✓" : "Location missing"} arrow><MyLocationIcon sx={{ fontSize: 14, color: user?.location ? "success.main" : "divider" }} /></Tooltip>
              </>
            )}

            <Box sx={{ width: "1px", height: 14, backgroundColor: "divider", mx: 0.3 }} />

            {/* Copy private URL */}
            <Tooltip title={urlCopied ? "Copied!" : "Copy your private login link"} arrow>
              <Box
                onClick={handleCopyUrl}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.3,
                  px: 0.8, py: 0.3, cursor: "pointer",
                  border: "1px solid", borderColor: "divider",
                  borderRadius: "3px", color: urlCopied ? "success.main" : "text.disabled",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "primary.main", color: "primary.main" },
                }}
              >
                {urlCopied ? <CheckCircleOutlineIcon sx={{ fontSize: 12 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
                <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.55rem", fontWeight: 500 }}>
                  {urlCopied ? "Copied" : "Copy link"}
                </Typography>
              </Box>
            </Tooltip>
            {/* Email private URL */}
            <Tooltip title="Email your login link to yourself" arrow>
              <Box
                onClick={() => {
                  const url = `${window.location.origin}/u/${user?.url_id}`;
                  window.open(`mailto:?subject=${encodeURIComponent("My Seriously login link")}&body=${encodeURIComponent(`Save this link to log back in to Seriously:\n\n${url}\n\nDo not share this with anyone — it gives full access to your account.`)}`, "_blank");
                }}
                sx={{
                  display: "flex", alignItems: "center",
                  px: 0.5, py: 0.3, cursor: "pointer",
                  borderRadius: "3px", color: "text.disabled",
                  transition: "all 0.2s",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <Typography sx={{ fontSize: "0.55rem" }}>✉</Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {pendingFeedbackMatch && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/conversations/${pendingFeedbackMatch}/feedback`)}
              >
                Complete Feedback
              </Button>
            }
          >
            Profile editing is locked until you complete your feedback.
          </Alert>
        )}

        {/* Discover action */}
        {isProfileComplete && (
          <Box
            onClick={() => navigate('/discover')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1.2, mb: 2,
              border: '1px solid', borderColor: 'divider',
              borderRadius: '2px', cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <ExploreIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.8rem', fontWeight: 500, color: 'text.primary' }}>
              Discover
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.7rem', color: 'text.disabled', flex: 1 }}>
              Browse profiles and find your person
            </Typography>
          </Box>
        )}

        {/* Good Vibes digests */}
        {digestsData?.digests?.length > 0 && (
          <GoodVibesDigest digests={digestsData.digests} onRefresh={refetchDigests} />
        )}


        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Photo */}
        <Box
          component="label"
          sx={{
            display: 'block', position: 'relative', mb: 2,
            borderRadius: '2px', overflow: 'hidden',
            cursor: uploading === 'photo' ? 'default' : 'pointer',
            border: '1px solid', borderColor: 'divider',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'primary.main' },
            '&:hover .photo-overlay': { opacity: 1 },
          }}
        >
          {user?.photo_url ? (
            <>
              <Box
                component="img"
                src={user.photo_url}
                alt="Profile"
                sx={{ display: 'block', width: '100%', height: { xs: 280, sm: 360 }, objectFit: 'cover' }}
              />
              {/* Hover overlay */}
              <Box
                className="photo-overlay"
                sx={{
                  position: 'absolute', inset: 0,
                  backgroundColor: 'rgba(26,22,20,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.2s',
                }}
              >
                {uploading === 'photo' ? (
                  <CircularProgress size={24} sx={{ color: '#faf8f5' }} />
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <PhotoCameraIcon sx={{ fontSize: 24, color: '#faf8f5', mb: 0.5 }} />
                    <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.65rem', color: '#faf8f5', fontWeight: 500 }}>
                      Change photo
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                height: { xs: 200, sm: 240 },
                backgroundColor: 'secondary.main',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 1,
              }}
            >
              {uploading === 'photo' ? (
                <CircularProgress size={20} sx={{ color: 'primary.main' }} />
              ) : (
                <>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    border: '1.5px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <PhotoCameraIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </Box>
                  <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.7rem', color: 'text.disabled' }}>
                    Add your photo
                  </Typography>
                </>
              )}
            </Box>
          )}
          <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} disabled={uploading === 'photo'} />
        </Box>

        {/* Voice Recording */}
        <Paper sx={{ px: 2, py: 1.5, mb: 2 }}>
          <VoiceRecorder
            onUpload={handleVoiceUpload}
            uploading={uploading === 'voice'}
            existingUrl={user?.voice_recording_url}
          />
        </Paper>

        {/* Words About Me */}
        <Paper sx={{ px: 2, py: 1.5, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: user?.tags?.length ? 1 : 0 }}>
            {/* Label */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 14, color: "text.disabled" }}>#</Typography>
              <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: "0.8rem", color: "text.primary" }}>
                Words
              </Typography>
            </Box>
            {/* Input */}
            <TextField
              size="small"
              variant="standard"
              placeholder="A word that describes you…"
              value={newTag}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes(' ')) {
                  const word = value.replace(/\s/g, '');
                  if (word) handleAddTag(word);
                  else setNewTag('');
                } else {
                  setNewTag(value);
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTag(); }}
              inputProps={{ maxLength: 20 }}
              InputProps={{ disableUnderline: false }}
              sx={{ flex: 1, '& .MuiInput-input': { fontSize: '0.8rem', py: 0.3 } }}
              disabled={addingTag}
            />
          </Box>
          {/* Tags */}
          {user?.tags && user.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {user.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() => handleRemoveTag(tag)}
                  disabled={removingTag === tag}
                  sx={{
                    backgroundColor: "text.primary",
                    color: '#faf8f5',
                    fontSize: '0.68rem',
                    '& .MuiChip-deleteIcon': { color: 'rgba(250,248,245,0.5)', fontSize: 14, '&:hover': { color: '#faf8f5' } },
                  }}
                />
              ))}
            </Box>
          )}
        </Paper>

        {/* Location */}
        <Box sx={{ mb: 2 }}>
          <LocationPicker
            initialLocation={
              user?.location
                ? { lat: user.location.lat, lng: user.location.lng, radius: user.location.radius }
                : undefined
            }
            onSave={handleLocationSave}
            saving={savingLocation}
          />
        </Box>


        {/* Phone number */}
        <Paper sx={{ px: 2, py: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.68rem", color: "text.disabled", flexShrink: 0 }}>📱</Typography>
            <TextField
              variant="standard"
              size="small"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+44 7123 456789 — we'll text you at launch"
              sx={{ flex: 1, '& .MuiInput-input': { fontSize: '0.8rem', py: 0.3 } }}
            />
            {phoneNumber !== (user?.phone_number || '') && (
              <Box
                onClick={savingPhone ? undefined : async () => {
                  setSavingPhone(true);
                  setError('');
                  try {
                    await api.put('/api/profile/phone', { phone_number: phoneNumber });
                    await fetchProfile();
                    showToast('Phone number updated');
                  } catch (err: any) {
                    setError(err.response?.data?.detail || 'Failed to update phone');
                  } finally {
                    setSavingPhone(false);
                  }
                }}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.3,
                  px: 0.8, py: 0.3, cursor: "pointer",
                  border: "1px solid", borderColor: "primary.main",
                  borderRadius: "2px", color: "primary.main",
                  transition: "all 0.2s", fontSize: "0.6rem", fontWeight: 500,
                  "&:hover": { backgroundColor: "rgba(192,57,43,0.04)" },
                }}
              >
                {savingPhone ? <CircularProgress size={10} /> : "Save"}
              </Box>
            )}
          </Box>
        </Paper>

        {/* ── Danger zone ──────────────────────────────────────── */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="overline" sx={{ color: "text.disabled", mb: 1.5, display: "block" }}>
          Account
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <ActionChip
            label={exporting ? "Exporting…" : "Export data"}
            icon={<DownloadIcon sx={{ fontSize: 12 }} />}
            onClick={async () => {
              setExporting(true); setError('');
              try {
                const res = await api.get('/api/profile/export');
                const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'seriously-my-data.json'; a.click();
                URL.revokeObjectURL(url); showToast('Data exported!');
              } catch (err: any) { setError(err.response?.data?.detail || 'Failed to export data'); }
              finally { setExporting(false); }
            }}
            disabled={exporting}
          />
          <ActionChip
            label="Log out"
            icon={<LogoutIcon sx={{ fontSize: 12 }} />}
            onClick={logout}
          />
          <ActionChip
            label="Delete account"
            icon={<DeleteForeverIcon sx={{ fontSize: 12 }} />}
            onClick={() => setDeleteDialogOpen(true)}
            danger
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, mb: 4 }}>
          <Link component={RouterLink} to="/privacy" target="_blank" sx={{ fontFamily: "'DM Sans'", fontSize: "0.6rem", color: "text.disabled", textDecoration: "none", "&:hover": { color: "primary.main" } }}>Privacy</Link>
          <Link component={RouterLink} to="/terms" target="_blank" sx={{ fontFamily: "'DM Sans'", fontSize: "0.6rem", color: "text.disabled", textDecoration: "none", "&:hover": { color: "primary.main" } }}>Terms</Link>
        </Box>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle sx={{ color: "primary.main", fontWeight: 'bold' }}>
            Delete Account Permanently?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              This action is irreversible. All your data will be permanently deleted:
            </Typography>
            <Typography component="ul" sx={{ pl: 2 }}>
              <li>Profile photo and voice recording</li>
              <li>Tags, location, and preferences</li>
              <li>All interactions and voice messages</li>
              <li>Photo and voice embeddings</li>
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
              You will not be able to recover your account.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await api.delete('/api/profile/account');
                  localStorage.removeItem('userId');
                  localStorage.removeItem('sessionToken');
                  window.location.href = '/';
                } catch (err: any) {
                  setError(err.response?.data?.detail || 'Failed to delete account');
                  setDeleteDialogOpen(false);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? <CircularProgress size={20} /> : 'Delete My Account'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

function ActionChip({ label, icon, onClick, danger, disabled }: {
  label: string; icon: React.ReactNode; onClick: () => void; danger?: boolean; disabled?: boolean;
}) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        px: 1.2, py: 0.5, cursor: disabled ? "default" : "pointer",
        border: "1px solid", borderColor: danger ? "error.main" : "divider",
        borderRadius: "2px", opacity: disabled ? 0.4 : 1,
        color: danger ? "error.main" : "text.disabled",
        transition: "all 0.2s",
        "&:hover": disabled ? {} : { borderColor: danger ? "error.dark" : "text.primary", color: danger ? "error.dark" : "text.primary" },
      }}
    >
      {icon}
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.65rem", fontWeight: 500 }}>{label}</Typography>
    </Box>
  );
}

export default Profile;
